'use client';
// Matériel — lots de réactifs & consommables (§6.6) : quarantaine, contrôle de
// réception et alerte péremption à trois niveaux. Les fournisseurs et leurs
// évaluations ont rejoint le carnet de contacts et la revue de contrat (L3, 10/10/2026).
import React, { useEffect, useMemo, useState } from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import { Tableau } from '@/shared/Tableau';
import { useGed } from '@/lib/store';
import { LOT_STATES, lotNiveau, lotNiveauMeta, joursRestants, vocabMeta, fmtDate } from '@/lib/meta';
import { I, card, inputStyle, FilterBar, FieldError, RequiredHint, Help, useBusy, Selecteur, Pastille } from '@/components/ui';
import type { GedLot } from '@/lib/types';
import { useFormulaire } from '@/components/Formulaire';

import { Bouton } from '@/shared/Bouton';
import { ChoixCorrespondant, contactsParIds } from '@/components/ChoixCorrespondant';
import { PiedActions } from '@/shared/PiedActions';
import { EtiquettesDeSerie } from '@/components/screens/Fractions';
const lbl: React.CSSProperties = { display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 5 };
const pill = (m: { label: string; bg: string; fg: string }) => (
  <span style={{ display: 'inline-flex', padding: '2px 9px', borderRadius: 'var(--radius-pill)', background: m.bg, color: m.fg, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{m.label}</span>
);

const AIDE_QUARANTAINE = 'Quarantaine : à la réception, le lot est isolé et ne peut pas être utilisé tant que le contrôle de réception n’a pas été enregistré. C’est l’état par défaut de tout lot entrant.';

/** Un lot encore en circulation : les lots épuisés ou retirés ne présentent
 *  plus de risque et sortent des alertes. */
const enCirculation = (l: GedLot) => !['epuise', 'retire'].includes(l.statut);

/** Badge de péremption à trois niveaux. Le rendu unique précédent affichait un
 *  lot périmé depuis trois mois exactement comme un lot utilisable jusqu'en
 *  septembre — enjeu direct de sécurité des analyses. */
function BadgePeremption({ lot }: { lot: GedLot }) {
  if (!enCirculation(lot)) return null;
  const niveau = lotNiveau(lot.datePeremption);
  if (!niveau) return null;
  const j = joursRestants(lot.datePeremption) ?? 0;
  const m = lotNiveauMeta(niveau, j);
  /* L9 : sur la Pastille du socle (É-12) ; la couleur vient du niveau (`lotNiveauMeta`), un sens métier. */
  return <Pastille bg={m.bg} fg={m.fg} compacte>{niveau === 'perime' && <I n="alert" s={11} />}{m.label}</Pastille>;
}

/** Ce que le catalogue dit d'un produit, lu pour la réception (M3). */
type ProduitCatalogue = {
  code: string; libelle: string; famille: string; fournisseurCode: string; fournisseurNom: string; discipline: string; equipementCode: string; actif: boolean;
  fractionnement: { actif: boolean; unite: string; nbParDefaut: number | null; volumeUl: number | null; stabiliteCongeleJours: number | null; stabiliteDecongeleHeures: number | null; temperature: string };
  fractionnementLibelle: string;
};
const FAMILLE_LIBELLE: Record<string, string> = { reactif: 'Réactif', controle: 'Contrôle', calibrant: 'Calibrant', consommable: 'Consommable' };

/** Le geste « Fractionner » sur un lot accepté (M3, 10/10/2026) : le catalogue
 *  propose, le lot fait foi, la saisie tranche — et tout est RECOPIÉ sur la
 *  série, qui fige. Le serveur refuse en le disant (quarantaine, produit non
 *  fractionnable, date future). */
function FractionnerLot({ lot, produit, onFait, onAnnule }: { lot: GedLot; produit: ProduitCatalogue; onFait: (codeSerie: string) => void; onAnnule: () => void }) {
  const { showToast, go } = useGed();
  const racine = React.useRef<HTMLDivElement>(null);
  useEffect(() => { racine.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }, []);
  const cap = produit.fractionnement;
  const origine = lot.stabiliteCongeleJours != null || lot.stabiliteDecongeleHeures != null ? 'lot' : 'catalogue';
  const [f, setF] = useState({
    nombre: cap.nbParDefaut != null ? String(cap.nbParDefaut) : '', volumeUl: cap.volumeUl != null ? String(cap.volumeUl) : '', dateFractionnement: '',
    stabiliteCongeleJours: String((origine === 'lot' ? lot.stabiliteCongeleJours : cap.stabiliteCongeleJours) ?? ''),
    stabiliteDecongeleHeures: String((origine === 'lot' ? lot.stabiliteDecongeleHeures : cap.stabiliteDecongeleHeures) ?? ''),
    temperature: cap.temperature || '', remarque: '',
  });
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();
  const unite = f.nombre === '1' ? cap.unite : `${cap.unite}s`;
  return (
    <div ref={racine} style={{ ...card, padding: 16, display: 'grid', gap: 10, borderInlineStart: '3px solid var(--cyan-600)' }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-strong)' }}>Fractionner le lot {lot.ref} — {produit.libelle}</div>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
        Le catalogue propose <strong>{produit.fractionnementLibelle}</strong>{origine === 'lot' ? ' ; les stabilités ci-dessous sont celles saisies sur le lot à la réception.' : ' ; les stabilités sont celles du catalogue — vérifiez-les sur la notice.'} Tout est figé sur la série.
      </div>
      <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px 16px' }}>
        <div><label style={lbl} htmlFor="fl-nb">Nombre de {`${cap.unite}s`} <span style={{ color: 'var(--red-600)' }}>*</span></label>
          <input id="fl-nb" type="number" min={1} max={500} value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} style={inputStyle} /></div>
        <div><label style={lbl} htmlFor="fl-vol">Volume d’une {cap.unite} (µL)</label>
          <input id="fl-vol" type="number" min={1} value={f.volumeUl} onChange={(e) => setF({ ...f, volumeUl: e.target.value })} style={inputStyle} /></div>
        <div><label style={lbl} htmlFor="fl-date">Fractionné le</label>
          <input id="fl-date" type="date" value={f.dateFractionnement} onChange={(e) => setF({ ...f, dateFractionnement: e.target.value })} style={inputStyle} />
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4 }}>Vide = aujourd’hui.</div></div>
        <div><label style={lbl} htmlFor="fl-sc">Stabilité d’une fraction en stock (jours)</label>
          <input id="fl-sc" type="number" min={0} value={f.stabiliteCongeleJours} onChange={(e) => setF({ ...f, stabiliteCongeleJours: e.target.value })} style={inputStyle} /></div>
        <div><label style={lbl} htmlFor="fl-sd">Stabilité après sortie (heures)</label>
          <input id="fl-sd" type="number" min={0} value={f.stabiliteDecongeleHeures} onChange={(e) => setF({ ...f, stabiliteDecongeleHeures: e.target.value })} style={inputStyle} /></div>
        <div><label style={lbl} htmlFor="fl-temp">Conservées à</label>
          <input id="fl-temp" value={f.temperature} onChange={(e) => setF({ ...f, temperature: e.target.value })} style={inputStyle} placeholder="−20 °C, 2 – 8 °C…" /></div>
      </div>
      <div><label style={lbl} htmlFor="fl-rem">Remarque</label><input id="fl-rem" value={f.remarque} onChange={(e) => setF({ ...f, remarque: e.target.value })} style={inputStyle} /></div>
      <FieldError>{erreur}</FieldError>
      <PiedActions taille="sm" annuler={{ onClick: onAnnule }} principale={{ libelle: `Tirer ${f.nombre || '…'} ${unite}`, libelleChargement: 'Enregistrement…', chargement: estOccupe('frac'), disabled: estOccupe() || !f.nombre, onClick: () => void run('frac', async () => {
        setErreur('');
        const r = await fetchEcriture(`/api/ged/ged/lots/${lot.documentId}/fractionner`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(f) });
        const d = await r.json().catch(() => null);
        if (!r.ok) { setErreur(d?.error?.message || 'Fractionnement refusé.'); return; }
        showToast(`${d.nombre} ${unite} tirée(s) du lot ${lot.ref} — série ${d.codeLisible}${d.manques?.length ? ` (${d.manques.join(', ')})` : ''}.`, 'info', { label: 'Voir le fractionnement', faire: () => go('mat') });
        onFait(d.codeSerie);
      }) }} />
    </div>
  );
}

export function Lots() {
  const { lots, session, smq, settings, loading, booted, equips, refresh } = useGed();
  const canWork = session.roles.length > 0;
  const [showNew, setShowNew] = useState(false);
  const NL_VIDE = { produitCode: '', designation: '', fournisseur: '', fournisseurCode: '', equipementCode: '', discipline: '', numeroLot: '', datePeremption: '', stabiliteCongeleJours: '', stabiliteDecongeleHeures: '' };
  const [nl, setNl] = useState(NL_VIDE);
  /* LE CATALOGUE (M3) : lu une fois, pour proposer le produit à la réception et
     savoir quel lot se fractionne. Une lecture en échec se dit, elle ne laisse
     pas croire que rien n'est fractionnable. */
  const [produits, setProduits] = useState<ProduitCatalogue[] | null>(null);
  const [erreurCatalogue, setErreurCatalogue] = useState('');
  useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/produits', { credentials: 'include' })
      .then(async (r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => { if (vivant) setProduits((d?.lignes || []) as ProduitCatalogue[]); })
      .catch((e) => { if (vivant) { setProduits([]); setErreurCatalogue(String(e?.message || e)); } });
    return () => { vivant = false; };
  }, []);
  const produitDe = (code?: string | null) => (code ? produits?.find((p) => p.code === code) || null : null);
  const [aFractionner, setAFractionner] = useState<GedLot | null>(null);
  /* La série qu'on vient de tirer : sa fenêtre d'étiquettes s'ouvre aussitôt,
     sans impression automatique (la Zebra peut être absente). */
  const [etiquettesDe, setEtiquettesDe] = useState<string | null>(null);
  const choisirProduit = (code: string) => {
    const p = produitDe(code);
    if (!p) { setNl({ ...nl, produitCode: '' }); return; }
    /* Le produit PROPOSE ; ce qui est déjà tapé n'est pas écrasé par du vide. */
    setNl({
      ...nl, produitCode: code, designation: p.libelle, fournisseurCode: p.fournisseurCode || nl.fournisseurCode, fournisseur: p.fournisseurNom || nl.fournisseur,
      equipementCode: p.equipementCode || nl.equipementCode, discipline: p.discipline || nl.discipline,
      stabiliteCongeleJours: p.fractionnement?.actif && p.fractionnement.stabiliteCongeleJours != null ? String(p.fractionnement.stabiliteCongeleJours) : '',
      stabiliteDecongeleHeures: p.fractionnement?.actif && p.fractionnement.stabiliteDecongeleHeures != null ? String(p.fractionnement.stabiliteDecongeleHeures) : '',
    });
    if (erreur) setErreur('');
  };
  const produitChoisi = produitDe(nl.produitCode);
  const [erreur, setErreur] = useState('');
  const [q, setQ] = useState('');
  const [filtre, setFiltre] = useState('tous');
  const { run, estOccupe } = useBusy();
  const discs = settings?.disciplines?.filter((d) => d.active) || [];
  const enChargement = !booted || (loading && lots.length === 0);

  const perimes = lots.filter((l) => enCirculation(l) && lotNiveau(l.datePeremption) === 'perime');
  const critiques = lots.filter((l) => enCirculation(l) && lotNiveau(l.datePeremption) === 'critique');
  const surveiller = lots.filter((l) => enCirculation(l) && lotNiveau(l.datePeremption) === 'surveiller');
  const quarantaine = lots.filter((l) => l.statut === 'quarantaine');

  const listeTriee = useMemo(() => {
    const base = filtre === 'perimes' ? perimes
      : filtre === 'critiques' ? critiques
        : filtre === 'surveiller' ? surveiller
          : filtre === 'quarantaine' ? quarantaine
            : lots;
    const qq = q.trim().toLowerCase();
    const cherche = qq ? base.filter((l) => `${l.ref} ${l.designation} ${l.numeroLot} ${l.fournisseur} ${l.discipline}`.toLowerCase().includes(qq)) : base;
    // Périmés en tête : c'est le seul ordre qui met le risque sous les yeux.
    const rang = (l: GedLot) => {
      if (!enCirculation(l)) return 9;
      const n = lotNiveau(l.datePeremption);
      return n ? lotNiveauMeta(n, 0).rang : 5;
    };
    return [...cherche].sort((a, b) => {
      const ra = rang(a), rb = rang(b);
      if (ra !== rb) return ra - rb;
      return (a.datePeremption || '').localeCompare(b.datePeremption || '');
    });
  }, [lots, filtre, q, perimes, critiques, surveiller, quarantaine]);

  const declarationLot = React.useMemo(() => [
    { id: 'l-des', label: 'Désignation du lot', valeur: nl.designation, requis: true },
  ], [nl.designation]);
  const { ref: refLot, verifier: verifierLot, Bandeau: BandeauLot } = useFormulaire(declarationLot);

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {canWork && (showNew ? (
        <div ref={refLot} style={{ ...card, padding: 16, display: 'grid', gap: 10 }}>
          <BandeauLot />
          <RequiredHint />
          <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={lbl} htmlFor="l-prod">Produit du catalogue</label>
              {/* Le catalogue PROPOSE désignation, fournisseur, équipement, discipline et
                  stabilités ; c'est lui qui dit si le lot se fractionnera (M3, 10/10/2026). */}
              <Selecteur id="l-prod" value={nl.produitCode} onChange={choisirProduit} style={inputStyle}>
                <option value="">— hors catalogue (désignation libre, lot non fractionnable) —</option>
                {(produits || []).filter((p) => p.actif).map((p) => <option key={p.code} value={p.code}>{FAMILLE_LIBELLE[p.famille] || p.famille} · {p.libelle}{p.fractionnement?.actif ? ` — ${p.fractionnementLibelle}` : ''}</option>)}
              </Selecteur>
              {erreurCatalogue && <div style={{ fontSize: 12, color: 'var(--red-600)', marginTop: 4 }}>Catalogue illisible ({erreurCatalogue}) — réceptionnez hors catalogue ou rechargez la page.</div>}
              {produits && produits.length === 0 && !erreurCatalogue && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Le catalogue des produits est vide : référencez-y les produits pour qu’un lot se relie et se fractionne.</div>}
            </div>
            <div>
              <label style={lbl} htmlFor="l-des">Désignation <span style={{ color: 'var(--red-600)' }}>*</span></label>
              <input id="l-des" value={nl.designation} onChange={(e) => { setNl({ ...nl, designation: e.target.value }); if (erreur) setErreur(''); }}
                aria-invalid={!!erreur} style={{ ...inputStyle, borderColor: erreur ? 'var(--red-600)' : undefined }} />
              <FieldError>{erreur}</FieldError>
            </div>
            <div><label style={lbl}>N° de lot</label><input value={nl.numeroLot} onChange={(e) => setNl({ ...nl, numeroLot: e.target.value })} style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} /></div>
            <div><label style={lbl} htmlFor="lot-fourn">Fournisseur</label>
              {/* Choisi au CARNET (L4, 10/10/2026), plus tapé : le nom suit le code. */}
              <ChoixCorrespondant id="lot-fourn" valeur={nl.fournisseurCode} familles={['fournisseurs']} vide="— fournisseur de l’annuaire —"
                onChange={(code) => { setNl({ ...nl, fournisseurCode: code, fournisseur: '' }); if (code) void contactsParIds([code]).then((m) => setNl((x) => (x.fournisseurCode === code ? { ...x, fournisseur: m.get(code)?.nom || '' } : x))); }}
                aide="Un fournisseur absent se crée d’abord dans l’annuaire (tuile « Fournisseur »)." /></div>
            <div><label style={lbl}>Discipline</label><Selecteur value={nl.discipline} onChange={(v) => setNl({ ...nl, discipline: v })} style={inputStyle}><option value="">—</option>{discs.map((d) => <option key={d.name} value={d.name}>{d.name}</option>)}</Selecteur></div>
            <div><label style={lbl} htmlFor="lot-equip">Équipement servi</label>
              {/* Un réactif se relie à l'appareil qu'il alimente (RQ, 10/10/2026) ; vide pour un consommable. */}
              <Selecteur id="lot-equip" value={nl.equipementCode} onChange={(v) => setNl({ ...nl, equipementCode: v })} style={inputStyle}>
                <option value="">— aucun (consommable) —</option>
                {equips.filter((e) => e.code).map((e) => <option key={e.code} value={e.code!}>{e.code} · {e.nom}</option>)}
              </Selecteur></div>
            <div>
              <label style={lbl}>Péremption</label>
              <input type="date" value={nl.datePeremption} onChange={(e) => setNl({ ...nl, datePeremption: e.target.value })} style={inputStyle} />
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 5 }}>Sans date, aucune alerte de péremption ne pourra être émise pour ce lot.</div>
            </div>
            {produitChoisi?.fractionnement?.actif && (
              <div style={{ gridColumn: '1 / -1', display: 'grid', gap: 8, padding: '10px 12px', background: 'var(--cyan-50)', borderRadius: 'var(--radius-sm)' }}>
                <div style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
                  Ce produit se fractionne : <strong>{produitChoisi.fractionnementLibelle}</strong>. Les stabilités ci-dessous viennent du catalogue —
                  <strong> le lot fait foi</strong> : confirmez-les notice en main, elles dateront chaque fraction.
                </div>
                <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' }}>
                  <div><label style={lbl} htmlFor="l-sc">Stabilité d’une fraction en stock (jours)</label>
                    <input id="l-sc" type="number" min={0} value={nl.stabiliteCongeleJours} onChange={(e) => setNl({ ...nl, stabiliteCongeleJours: e.target.value })} style={inputStyle} /></div>
                  <div><label style={lbl} htmlFor="l-sd">Stabilité après sortie (heures)</label>
                    <input id="l-sd" type="number" min={0} value={nl.stabiliteDecongeleHeures} onChange={(e) => setNl({ ...nl, stabiliteDecongeleHeures: e.target.value })} style={inputStyle} /></div>
                </div>
              </div>
            )}
          </div>
          <PiedActions taille="sm" annuler={{ onClick: () => { setShowNew(false); setErreur(''); } }} principale={{ libelle: 'Réceptionner le lot', libelleChargement: 'Enregistrement…', chargement: estOccupe('new'), disabled: estOccupe(), onClick: () => void run('new', async () => {
              if (!verifierLot()) return;
              setErreur('');
              const ok = await smq('ged/lots', nl, 'Lot enregistré — en quarantaine jusqu’au contrôle de réception.');
              if (ok) { setShowNew(false); setNl(NL_VIDE); }
            }) }} />
        </div>
      ) : (
        <Bouton onClick={() => setShowNew(true)} style={{ width: 'fit-content' }}><I n="plus" s={14} />Réceptionner un lot</Bouton>
      ))}

      {aFractionner && produitDe(aFractionner.produitCode) && (
        <FractionnerLot lot={aFractionner} produit={produitDe(aFractionner.produitCode) as ProduitCatalogue}
          onFait={(codeSerie) => { setAFractionner(null); setEtiquettesDe(codeSerie); void refresh(); }} onAnnule={() => setAFractionner(null)} />
      )}

      {etiquettesDe && <EtiquettesDeSerie codeSerie={etiquettesDe} onClose={() => setEtiquettesDe(null)} />}

      {perimes.length > 0 && (
        <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--red-50)', border: '1px solid var(--red-600)', borderRadius: 'var(--radius-md)', padding: '10px 14px', color: 'var(--red-600)', fontSize: 13, fontWeight: 600 }}>
          <I n="alert" s={16} />
          {perimes.length} lot(s) périmé(s) encore en circulation — à retirer du service.
        </div>
      )}

      <FilterBar
        q={q} onQ={setQ} placeholder="Rechercher un lot, un n° ou un fournisseur…"
        chips={[
          { k: 'tous', label: 'Tous', n: lots.length },
          { k: 'perimes', label: 'Périmés', n: perimes.length },
          { k: 'critiques', label: '≤ 30 j', n: critiques.length },
          { k: 'surveiller', label: '≤ 60 j', n: surveiller.length },
          { k: 'quarantaine', label: 'En quarantaine', n: quarantaine.length },
        ]}
        actif={filtre} onChip={setFiltre}
        total={lots.length} affiches={listeTriee.length}
        onReset={() => { setQ(''); setFiltre('tous'); }} />

      <div style={{ ...card, overflow: 'hidden' }}>
        {(
          <Tableau
            id="lots-reactifs"
            ajusterLargeur
            /* L6 (10/10/2026) : niveau STANDARD — filtres (discipline, statut en liste ;
               péremption du/au), masquage, états intégrés, transitions d'état en
               colonne d'actions. */
            niveau="standard"
            chargement={enChargement}
            vide={{
              titre: lots.length === 0 ? 'Aucun lot enregistré.' : 'Aucun lot ne correspond à ces critères.',
              indice: lots.length === 0 ? 'Tout lot réceptionné entre en quarantaine jusqu’au contrôle de réception.' : 'Élargissez la recherche ou revenez à « Tous ».',
              action: lots.length > 0
                ? <Bouton taille="sm" onClick={() => { setQ(''); setFiltre('tous'); }}>Réinitialiser les filtres</Bouton>
                : undefined,
            }}
            lignes={listeTriee}
            cleLigne={(l) => l.documentId}
            ligneProps={(l) => ({
              style: enCirculation(l) && lotNiveau(l.datePeremption) === 'perime' ? { background: 'var(--red-50)' } : undefined,
            })}
            colonnes={[
              {
                cle: 'ref', libelle: 'Réf.', valeur: (l) => l.ref,
                rendu: (l) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 600, color: 'var(--color-primary-active)' }}>{l.ref}</span>,
              },
              {
                cle: 'designation', libelle: 'Désignation', valeur: (l) => l.designation,
                rendu: (l) => (
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-strong)' }}>{l.designation}</span>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{l.numeroLot} · {l.fournisseur}</span>
                    {produitDe(l.produitCode)?.fractionnement?.actif && (
                      <span style={{ display: 'block', fontSize: 11, color: 'var(--cyan-700)' }}>{produitDe(l.produitCode)?.fractionnementLibelle}</span>
                    )}
                  </span>
                ),
              },
              { cle: 'discipline', libelle: 'Discipline', type: 'liste', valeur: (l) => l.discipline, rendu: (l) => <span style={{ fontSize: 12.5 }}>{l.discipline}</span> },
              {
                // Le TRI porte sur la date ISO brute — c'est la colonne pour
                // laquelle le tri a été demandé : « quel lot périme en premier ? »
                cle: 'peremption', libelle: 'Péremption', type: 'date', valeur: (l) => l.datePeremption || '',
                rendu: (l) => (
                  <span style={{ display: 'grid', gap: 3, justifyItems: 'start' }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>{fmtDate(l.datePeremption)}</span>
                    <BadgePeremption lot={l} />
                  </span>
                ),
              },
              {
                cle: 'statut', libelle: 'Statut', type: 'liste', valeur: (l) => l.statut, libelleValeur: (v) => vocabMeta(LOT_STATES, v).label,
                rendu: (l) => (l.statut === 'quarantaine'
                  ? <Help aide={AIDE_QUARANTAINE}>{pill(vocabMeta(LOT_STATES, l.statut))}</Help>
                  : pill(vocabMeta(LOT_STATES, l.statut))),
              },
            ]}
            actions={(l) => {
                  const next: Record<string, { to: string; label: string }[]> = {
                    quarantaine: [{ to: 'accepte', label: 'Accepter (contrôle OK)' }, { to: 'retire', label: 'Retirer' }],
                    accepte: [{ to: 'en_service', label: 'Mettre en service' }, { to: 'retire', label: 'Retirer' }],
                    en_service: [{ to: 'epuise', label: 'Épuisé' }, { to: 'retire', label: 'Retirer' }],
                  };
                  const fractionnable = ['accepte', 'en_service'].includes(l.statut) && !!produitDe(l.produitCode)?.fractionnement?.actif;
                  return (
                    <span style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                      {canWork && fractionnable && (
                        <Bouton variante="contour" taille="sm" onClick={() => setAFractionner(l)} disabled={estOccupe()} aria-expanded={aFractionner?.documentId === l.documentId}>Fractionner</Bouton>
                      )}
                      {canWork && (next[l.statut] || []).map((n) => (
                        <Bouton variante={n.to === 'retire' ? 'danger' : 'contour'} taille="sm" key={n.to} onClick={() => void run(`${l.documentId}-${n.to}`, () => smq(`ged/lots/${l.documentId}/status`, { statut: n.to }, `Lot ${l.ref} — ${n.label.toLowerCase()}.`))} disabled={estOccupe()}>
                          {estOccupe(`${l.documentId}-${n.to}`) ? '…' : n.label}
                        </Bouton>
                      ))}
                    </span>
                  );
            }}
          />
        )}
      </div>
    </div>
  );
}

/* L'enveloppe « Matériel » vit dans `Materiel.tsx` depuis M1 (10/10/2026) :
   ce fichier ne porte plus que les LOTS. */
